import type { RecordView } from '@kit/sync/types/record-view';
import { Check, ExternalLink, RotateCcw } from 'lucide-react';
import { CategoryIcon } from '@/entities/shared/category/category-icon';
import { DueBadge } from '@/entities/shared/due/due-badge';
import { PriorityBadge } from '@/entities/shared/priority/priority-badge';
import { RecordTitle } from '@/entities/shared/record/record-title';
import { patchShoppingItem } from '@/entities/shopping-item/api/patch-shopping-item';
import { ShoppingPrice } from '@/entities/shopping-item/components/shopping-price';
import { neededDue } from '@/entities/shopping-item/helpers/needed-due';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { ListRow } from '@/shared/components/list/list-row';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { formatQuantity } from '@/shared/lib/money/format-quantity';
import { Button } from '@/shared/ui/button/button';

type ShoppingRowProps = {
  readonly view: RecordView<ShoppingItem>;
  readonly onOpen: () => void;
  readonly onBuy: () => void;
};

export function ShoppingRow({ view, onOpen, onBuy }: ShoppingRowProps) {
  const { t } = useTranslation();
  const entry = view.data;
  const pending = entry.status === 'pending';
  const meta = [
    formatQuantity(entry.quantity, entry.unit),
    entry.store,
    !pending && entry.bought_on ? t('bought on {date}', { date: formatDate(entry.bought_on) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <ListRow
      onClick={onOpen}
      muted={!pending}
      leading={<CategoryIcon category={entry.category} />}
      title={<RecordTitle pending={view.pending}>{entry.name}</RecordTitle>}
      subtitle={meta}
      status={
        pending ? (
          <>
            <PriorityBadge priority={entry.priority} />
            <DueBadge due={neededDue(entry)} />
          </>
        ) : undefined
      }
      trailing={
        <>
          <ShoppingPrice item={entry} />
          {entry.url && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('Open link')}
              className="max-sm:hidden"
              nativeButton={false}
              render={<a href={entry.url} target="_blank" rel="noreferrer noopener" />}
            >
              <ExternalLink />
            </Button>
          )}
          {pending ? (
            <Button
              variant="outline"
              size="icon"
              aria-label={t('Mark {name} as bought', { name: entry.name })}
              onClick={onBuy}
            >
              <Check />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('Back to pending')}
              onClick={() => void patchShoppingItem(view.id, { status: 'pending' })}
            >
              <RotateCcw />
            </Button>
          )}
        </>
      }
    />
  );
}
